import { Card, Col, Row } from 'react-bootstrap';

// What the standard says this drum was supposed to be.
//
// Read from the job's own `phasePlan`, not fetched: the spec is copied into the
// job when it is launched, so this is what the drum was actually made against
// rather than what the standard says today. A standard edited after the fact
// would otherwise quietly rewrite history.
//
// TANDEM is the case worth care. On the L120 the interior and exterior sheath
// are extruded in ONE pass, fused into a single task carrying
// `tandemPhases: ['interior_sheat', 'exterior_sheat']` — so one drum has TWO
// specs, and showing only the task's own phase would hide half of what was made.
//
// Which gauge measures which layer is settled from the data, not from the
// letters F and J: over 30 days there were 1 827 five-minute windows with both
// gauges measuring, and the jacketing gauge read larger than the filler in
// 1 827 of them. A tandem lays the inner sheath and then the outer over it, so
// filler = the inner layer, jacketing = the outer.
const L120_GAUGE_FOR_PHASE = {
  interior_sheat: { label: 'Filler gauge', set: 'FILLING_SET_DIA', act: 'FILLING_DIA_ACT_VALUE' },
  exterior_sheat: { label: 'Jacketing gauge', set: 'JACKETING_SET_DIA', act: 'JACKETING_DIA_ACT_VALUE' }
};

const PHASE_LABEL = {
  conductor_isolation: 'Isolation',
  pre_stranding: 'Pre-stranding',
  rope: 'Rope',
  interior_sheat: 'Inner sheath',
  exterior_sheat: 'Outer sheath',
  armouring: 'Armouring'
};

const mm = v => (v == null || v === '' ? null : `${Number(v).toFixed(2)} mm`);
const mm2 = v => (v == null || v === '' ? null : `${Number(v).toFixed(3)} mm²`);

const Field = ({ label, value, tone }) =>
  value == null ? null : (
    <div className="me-4 mb-1">
      <div className="fs-10 text-700">{label}</div>
      <div className={`fs-9 fw-semibold ${tone || ''}`}>{value}</div>
    </div>
  );

/** The wall the standard allows, and where the line actually sat inside it. */
const WallBand = ({ minRadial, maxRadial, measured, setDia, specDia }) => {
  if (minRadial == null && maxRadial == null) return null;
  return (
    <div className="me-4 mb-1">
      <div className="fs-10 text-700">Wall (radial)</div>
      <div className="fs-9 fw-semibold">
        {minRadial != null ? Number(minRadial).toFixed(2) : '—'}
        {' – '}
        {maxRadial != null ? Number(maxRadial).toFixed(2) : '—'} mm
      </div>
      {measured != null && specDia != null && (
        <div className={`fs-10 ${Math.abs(measured - specDia) > (maxRadial ?? 0.2) ? 'text-danger' : 'text-success'}`}>
          made {measured.toFixed(2)} mm ({measured - specDia >= 0 ? '+' : ''}
          {(measured - specDia).toFixed(2)} vs spec)
        </div>
      )}
    </div>
  );
};

/** One phase of the standard, laid out for whatever shape that phase has. */
const PhaseSpec = ({ phase, plan, machineId, summary, tokenColor }) => {
  if (!plan) {
    return (
      <div className="fs-9 text-700">
        {PHASE_LABEL[phase] || phase}: the job carries no plan for this phase.
      </div>
    );
  }

  const gauge = machineId === 'l120' ? L120_GAUGE_FOR_PHASE[phase] : null;
  const measured = gauge && summary?.[gauge.act]?.kind === 'number' ? summary[gauge.act].mean : null;
  const setDia = gauge && summary?.[gauge.set]?.kind === 'number' ? summary[gauge.set].mean : null;

  // `conductor_isolation` comes in two shapes across the standards — a
  // `conductors[]` list, or a flat `wire` + `isolation` pair on FY/MYF.
  const conductors =
    phase === 'conductor_isolation'
      ? Array.isArray(plan.conductors) && plan.conductors.length
        ? plan.conductors
        : plan.wire || plan.isolation
          ? [{ wire: plan.wire, isolation: plan.isolation }]
          : []
      : [];

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center gap-2 mb-1">
        <span className="fw-semibold fs-9">{PHASE_LABEL[phase] || phase}</span>
        {gauge && (
          <span className="fs-10 text-700">
            · measured by the {gauge.label.toLowerCase()}
          </span>
        )}
      </div>

      {conductors.length ? (
        conductors.map((c, i) => {
          // On a merged/grouped drum the token is one colour, so the conductor
          // that matches it is the one this drum actually is.
          const dim = tokenColor && c.isolation?.color && c.isolation.color !== tokenColor;
          return (
            <div key={i} className={`d-flex flex-wrap ${dim ? 'opacity-50' : ''}`}>
              <Field label="Wire Ø" value={mm(c.wire?.diameter)} />
              <Field label="Wire section" value={mm2(c.wire?.section)} />
              <Field label="Over insulation Ø" value={mm(c.isolation?.diameter)} />
              <Field label="Insulation section" value={mm2(c.isolation?.section)} />
              <WallBand
                minRadial={c.isolation?.minRadial}
                maxRadial={c.isolation?.maxRadial}
                specDia={c.isolation?.diameter}
                measured={measured}
              />
              <Field label="Colour" value={c.isolation?.color || null} />
            </div>
          );
        })
      ) : (
        <div className="d-flex flex-wrap">
          <Field label="Ø" value={mm(plan.diameter)} />
          <Field label="Section" value={mm2(plan.section)} />
          <Field label="Thickness" value={mm(plan.thickness)} />
          <Field label="Filling section" value={mm2(plan.fillingSection)} />
          <WallBand
            minRadial={plan.minRadial}
            maxRadial={plan.maxRadial}
            specDia={plan.diameter}
            measured={measured}
          />
          {setDia != null && (
            <Field
              label="Set on the line"
              value={mm(setDia)}
              tone={
                plan.diameter != null && Math.abs(setDia - plan.diameter) > 0.15
                  ? 'text-warning'
                  : ''
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * @param phasePlan  the job's own copy of the standard, `phasePlan[]`
 * @param task       the task that produced this drum — its `tandemPhases` is
 *                   what makes one drum carry two specs
 * @param summary    the run summary, so the spec can be put next to what the
 *                   line actually made
 */
const TokenPhaseSpec = ({ phasePlan, task, token, machineId, summary }) => {
  // A tandem task's own `phase` is the exterior; naming only that would hide the
  // inner sheath, which was extruded in the same pass and is half the product.
  const phases =
    task?.tandem && Array.isArray(task.tandemPhases) && task.tandemPhases.length
      ? task.tandemPhases
      : [task?.phase || token?.phase].filter(Boolean);

  if (!phases.length) return null;

  const byName = new Map((Array.isArray(phasePlan) ? phasePlan : []).map(p => [p.name, p]));

  return (
    <Card className="mb-3">
      <Card.Body className="py-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="fs-9 fw-semibold">To standard</span>
          {task?.tandem && (
            <span className="fs-10 text-warning">
              tandem — both layers were extruded in one pass
            </span>
          )}
        </div>
        <Row className="g-0">
          <Col xs={12}>
            {phases.map(p => (
              <PhaseSpec
                key={p}
                phase={p}
                plan={byName.get(p)}
                machineId={machineId}
                summary={summary}
                tokenColor={token?.color}
              />
            ))}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default TokenPhaseSpec;
